<template>
  <div
    :id="`${source}_date_time_filter`"
    :class="[`flex flex-row flex-wrap gap-2 items-center ${alignment}`]"
    @click.stop
  >
    <template v-if="selectedRange === 'custom'">
      <template v-if="isDateFormat">
        <a-date-picker
          v-model:value="startTime"
          :format="dateFormat"
          :show-time="isDateTime"
          :disabled-date="disabledStartDate"
          @change="handleDateChange"
          @click.stop
          placeholder="Select date"
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        />

        <template v-if="!singleDate">
          <span class="text-sm font-medium text_color">to</span>

          <a-date-picker
            v-model:value="endTime"
            :format="dateFormat"
            :show-time="isDateTime"
            :disabled-date="disabledEndDate"
            @change="handleDateChange"
            @click.stop
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          />
        </template>
      </template>

      <template v-else-if="isTimeFormat">
        <a-time-picker
          v-model:value="startTime"
          :format="dateFormat"
          @change="handleDateChange"
          @click.stop
          :getPopupContainer="(triggerNode) => triggerNode.parentNode"
        />
        <template v-if="!singleDate">
          <span>to</span>
          <a-time-picker
            v-model:value="endTime"
            :format="dateFormat"
            @change="handleDateChange"
            @click.stop
            :getPopupContainer="(triggerNode) => triggerNode.parentNode"
          />
        </template>
      </template>
    </template>
    <a-select
      :class="`${width}`"
      v-model:value="selectedRange"
      :options="dropdownOptions"
      @change="handleRangeChange"
      @click.stop
      placeholder="Select range"
      :getPopupContainer="(triggerNode) => triggerNode.parentNode"
    />
    <slot name="actions"></slot>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import dayjs from "dayjs";
import { message } from "ant-design-vue";

const props = defineProps({
  initialValue: { type: String, default: "last30days" },
  source: { type: String, required: true },
  width: { type: String, default: "w-[200px]" },
  alignment: { type: String, default: "flex-row" },
  parentFormat: { type: String, default: "YYYY-MM-DD" },
  initialRange: {
    type: Object,
    default: () => ({
      key: "last30days",
      start: null,
      end: null,
    }),
  },
  singleDate: { type: Boolean, default: false },
  extraOptions: { type: Array, default: () => [] },
});

const emit = defineEmits(["update:dateRange"]);

const isDateFormat = computed(() => /YYYY|MM|DD/.test(props.parentFormat));
const isTimeFormat = computed(() => /HH|mm|ss/.test(props.parentFormat));
const isDateTime = computed(() => isDateFormat.value && isTimeFormat.value);

const dateFormat = computed(() => props.parentFormat);

const selectedRange = ref(props.initialRange.key);
const startTime = ref(null);
const endTime = ref(null);

const baseDropdownOptions = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "lastMonth", label: "Last Month" },
];

const dropdownOptions = computed(() => [
  ...baseDropdownOptions,
  ...props.extraOptions,
  { value: "custom", label: "Custom" },
]);

const isValidDateRange = (start, end) => {
  const diffInYears = end.diff(start, "year", true);
  return diffInYears < 1;
};

const disabledStartDate = (current) => {
  if (!endTime.value) return false;
  return current && endTime.value.diff(current, "year", true) >= 1;
};

const disabledEndDate = (current) => {
  if (!startTime.value) return false;
  return current && current.diff(startTime.value, "year", true) >= 1;
};

const formatDate = (date) => {
  if (!date) return null;

  if (!isDateFormat.value && isTimeFormat.value) {
    return date.format(props.parentFormat);
  }

  if (isDateFormat.value && !isTimeFormat.value) {
    return date.format(props.parentFormat);
  }

  if (isDateTime.value) {
    return date.format(props.parentFormat);
  }

  return date.format(props.parentFormat);
};

const calculateDateRange = (range) => {
  const now = dayjs();
  let start, end;

  switch (range) {
    case "today":
      start = isTimeFormat.value ? now.startOf("day") : now.startOf("day");
      end = isTimeFormat.value ? now.endOf("day") : now.endOf("day");
      break;
    case "yesterday":
      start = now.subtract(1, "day").startOf("day");
      end = now.subtract(1, "day").endOf("day");
      break;
    case "last7days":
      start = now.subtract(6, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "last30days":
      start = now.subtract(29, "day").startOf("day");
      end = now.endOf("day");
      break;
    case "lastMonth":
      start = now.subtract(1, "month").startOf("month");
      end = now.subtract(1, "month").endOf("month");
      break;
    case "thisMonth":
      start = now.startOf("month");
      end = now.endOf("month");
      break;
    case "custom":
      start = startTime.value || now;
      end = endTime.value || now;
      if (!isValidDateRange(start, end)) {
        message.warning("Date range cannot exceed 1 year");
        end = start.add(1, "year").subtract(1, "day");
        endTime.value = end;
      }
      break;
    default:
      start = now.startOf("day");
      end = now.endOf("day");
  }

  return {
    start: formatDate(start),
    end: formatDate(end),
    key: range,
    label:
      range === "custom"
        ? `${formatDate(start)} to ${formatDate(end)}`
        : dropdownOptions.value.find((option) => option.value === range)?.label,
  };
};

const handleRangeChange = (value) => {
  selectedRange.value = value;

  if (value === "custom" && props.singleDate) {
    startTime.value = null;
    endTime.value = null;
    return;
  }

  if (value === "custom" && !startTime.value) {
    const now = dayjs();
    startTime.value = now.subtract(29, "day").startOf("day");
    endTime.value = now.endOf("day");
  } else {
    const range = calculateDateRange(value);
    startTime.value = dayjs(range.start);
    endTime.value = dayjs(range.end);
  }

  emitDateRange();
};

const handleDateChange = () => {
  if (props.singleDate && startTime.value) {
    endTime.value = startTime.value.endOf("day");
    emitDateRange();
    return;
  }
  if (startTime.value && endTime.value) {
    if (!isValidDateRange(startTime.value, endTime.value)) {
      message.warning("Date range cannot exceed 1 year");
      endTime.value = startTime.value.add(1, "year").subtract(1, "day");
    }
    emitDateRange();
  }
};

const emitDateRange = () => {
  const range = calculateDateRange(selectedRange.value);
  if (selectedRange.value) {
    emit("update:dateRange", {
      ...range,
      key: selectedRange.value,
    });
  }
};

const initializeFromProps = () => {
  if (props.initialRange) {
    let key = props.initialRange.key;
    selectedRange.value = key;

    if (
      selectedRange.value === "custom" &&
      props.initialRange.start &&
      props.initialRange.end
    ) {
      startTime.value = dayjs(props.initialRange.start);
      endTime.value = dayjs(props.initialRange.end);
    } else {
      const range = calculateDateRange(selectedRange.value);
      startTime.value = dayjs(range.start);
      endTime.value = dayjs(range.end);
    }
  } else {
    handleRangeChange(selectedRange.value);
  }
};

onMounted(() => {
  initializeFromProps();
  emitDateRange();
});
</script>
